import { Link } from "react-router-dom";
import { ArrowRight, Camera, Users, Heart, Baby } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";

const STUDIO_IMAGES = {
  studio: "/images/studio Dan.jpg",
  famille: "/images/univers-famille.jpg",
  couple: "/images/couple-3.jpg",
  enfants: "/images/univers-portrait.jpg",
};
export default function Studio() {
  return (
    <main data-testid="studio-page" className="pt-20">

      {/* 01 — INTRODUCTION */}
      <section className="relative min-h-[75vh] flex items-center overflow-hidden">
        <img
          src={STUDIO_IMAGES.studio}
          alt="Mon studio de photographie"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-black/65" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-28 w-full">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
              01 — Mon studio
            </p>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-5 max-w-4xl">
              Un espace privé pour
              <span className="gold-text italic block">
                créer vos souvenirs
              </span>
            </h1>

            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mt-8 max-w-2xl">
              Bienvenue dans mon studio maison, un espace chaleureux et
              créatif pensé pour les portraits de familles, de couples et
              d’enfants.
            </p>

            <Link to="/contact" className="btn-gold mt-10">
              Réserver une séance <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 02 — L'ESPACE */}
      <section
        className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24"
        data-testid="studio-space"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            number="02"
            eyebrow="Mon espace de création"
            title="Un studio maison à mon image"
            subtitle="Un espace privé où je peux prendre le temps de créer des portraits soignés, dans une ambiance confortable."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mt-16">

            <Reveal>
              <div className="relative">
                <div className="absolute -inset-4 border border-[#D4AF37]/25" />

                <img
                  src={STUDIO_IMAGES.studio}
                  alt="Installation du studio de Daniel Dulude"
                  className="relative w-full aspect-[4/3] object-cover"
                />
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div>
                <div className="w-14 h-14 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center mb-8">
                  <Camera
                    size={22}
                    className="text-[#D4AF37] -rotate-45"
                  />
                </div>

                <h2 className="font-serif text-3xl sm:text-4xl">
                  La lumière est au cœur de mon travail
                </h2>

                <p className="text-slate-400 leading-relaxed mt-6">
                  Mon installation me permet de travailler avec différentes
                  ambiances lumineuses et de créer des portraits adaptés à
                  chaque personne et à chaque séance.
                </p>

                <p className="text-slate-400 leading-relaxed mt-5">
                  Famille, couple ou enfant : je prends le temps de créer une
                  image qui vous ressemble.
                </p>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* 03 — FAMILLES / COUPLES / ENFANTS */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24"
        data-testid="studio-sessions"
      >
        <SectionHeading
          number="03"
          eyebrow="Mes séances au studio"
          title="Des portraits pour tous les moments"
          subtitle="Quelques exemples de ce que je réalise actuellement dans mon studio."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">

          {/* FAMILLES */}
          <Reveal>
            <div className="gold-card overflow-hidden h-full">
              <img
                src={STUDIO_IMAGES.famille}
                alt="Portrait de famille en studio"
                className="w-full aspect-[4/5] object-cover"
              />

              <div className="p-8">
                <Users size={22} className="text-[#D4AF37]" />

                <h3 className="font-serif text-2xl mt-4">
                  Familles
                </h3>

                <p className="text-slate-400 text-sm leading-relaxed mt-3">
                  Des souvenirs chaleureux à conserver et à partager.
                </p>
              </div>
            </div>
          </Reveal>

          {/* COUPLES */}
          <Reveal delay={0.12}>
            <div className="gold-card overflow-hidden h-full">
              <img
                src={STUDIO_IMAGES.couple}
                alt="Portrait de couple en studio"
                className="w-full aspect-[4/5] object-cover"
              />

              <div className="p-8">
                <Heart size={22} className="text-[#D4AF37]" />

                <h3 className="font-serif text-2xl mt-4">
                  Couples
                </h3>

                <p className="text-slate-400 text-sm leading-relaxed mt-3">
                  Des portraits qui mettent en valeur votre complicité.
                </p>
              </div>
            </div>
          </Reveal>

          {/* ENFANTS */}
          <Reveal delay={0.24}>
            <div className="gold-card overflow-hidden h-full">
              <img
                src={STUDIO_IMAGES.enfants}
                alt="Portrait d'enfant en studio"
                className="w-full aspect-[4/5] object-cover"
              />

              <div className="p-8">
                <Baby size={22} className="text-[#D4AF37]" />

                <h3 className="font-serif text-2xl mt-4">
                  Enfants
                </h3>

                <p className="text-slate-400 text-sm leading-relaxed mt-3">
                  Des portraits adaptés à leur personnalité et à leur énergie.
                </p>
              </div>
            </div>
          </Reveal>

        </div>
      </section>

      {/* 04 — CTA */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center"
        data-testid="studio-cta"
      >
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            04 — Votre séance
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5">
            Prêt à créer votre prochain souvenir ?
          </h2>

          <p className="text-slate-400 max-w-2xl mx-auto mt-6">
            Venez vivre une séance dans mon studio maison et créons ensemble
            des images qui vous ressemblent.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/tarifs" className="btn-outline-gold">
              Voir les tarifs
            </Link>

            <Link to="/contact" className="btn-gold">
              Réserver une séance <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>

    </main>
  );
}
